import React from 'react';
import { Link } from 'react-router-dom';

const OLD_VS_PARLI = [
  {
    old: (
      <>
        <strong>Fumbling through manuals</strong> looking for motion precedence.
      </>
    ),
    parli: (
      <>
        <strong>An active precedence stack</strong> that enforces proper order automatically.
      </>
    ),
  },
  {
    old: (
      <>
        <strong>Secretaries scrambling</strong> to type fast enough during heated debate.
      </>
    ),
    parli: (
      <>
        <strong>A live reactive feed</strong> that drafts formatted minutes as actions happen.
      </>
    ),
  },
  {
    old: (
      <>
        <strong>Awkward pauses</strong> while the Chair figures out the exact script.
      </>
    ),
    parli: (
      <>
        <strong>An AI Teleprompter</strong> giving the Chair the exact parliamentary script for every
        step.
      </>
    ),
  },
  {
    old: (
      <>
        <strong>20 minutes wasted</strong> voting on routine, non-controversial reports.
      </>
    ),
    parli: (
      <>
        <strong>One-click Consent Agendas</strong> with instant single-item extraction.
      </>
    ),
  },
];

const PILLARS = [
  {
    label: '01 · Rules engine',
    header: 'Parliamentary Procedure, Programmed to Perfection.',
    copy:
      'Never worry about out-of-order motions or improper voting thresholds again. Our Last-In, First-Out (LIFO) stack engine automatically validates motion ranks, tracks amendments, and pauses active debate when incidental interrupts occur.',
    points: [
      {
        title: 'Automated 7-Step Lifecycles',
        detail: 'Guides every motion smoothly from Made to Announced without skipping required steps.',
      },
      {
        title: 'FlowType Logic',
        detail:
          'Special handling for Points of Order, Appeals, and Inquiries—skipping unnecessary voting steps when immediate rulings are required.',
      },
      {
        title: 'Precedence Guardrails',
        detail:
          'Automatically blocks out-of-order main motions while higher-ranking business is pending.',
      },
    ],
  },
  {
    label: '02 · Dual command',
    header: 'Built for the Chair & Secretary Synergy.',
    copy:
      'Running a meeting is a team sport. While the Chair manages the room and speaker clocks, the Secretary captures the math and notes in real time.',
    points: [
      {
        title: 'The Secretary Console',
        detail:
          'Live-annotate motion text, enter Tellers’ Reports, and manage attendance without touching the Chair’s view.',
      },
      {
        title: 'The AI Chair Teleprompter',
        detail:
          'Auto-generated scripts adapt live to every meeting state—giving the Chair exact phrasing like “It is moved and seconded that…” or “The Ayes have it…”',
      },
      {
        title: 'Chair Dashboard',
        detail:
          'Experienced Chair who hates screens? Keep a lean script view on the chair’s device while the Secretary runs the console in the background.',
      },
    ],
  },
  {
    label: '03 · Consent fast-pass',
    header: 'Breeze Through Routine Business in 30 Seconds.',
    copy:
      'Stop spending half your agenda on routine approvals. Group standard reports into a single batch and adopt them in one click.',
    points: [
      {
        title: 'Instant Pull to Floor',
        detail:
          'Any member can request to pull an item for full debate with a single click—no second or vote required.',
      },
      {
        title: 'Automatic Multiplier',
        detail:
          'Resolving a consent agenda automatically splits items in the backend, logging each approval into the official minutes.',
      },
    ],
  },
  {
    label: '04 · Guest & room view',
    header: 'Complete Transparency for Members & Audience.',
    copy: 'Keep your assembly informed and engaged without room disruption.',
    points: [
      {
        title: 'Frictionless QR Code Access',
        detail:
          'Members scan a code on their phone to open a read-only live feed of the active motion and speaker clock.',
      },
      {
        title: 'Zero Confusion',
        detail:
          'Audience members see exactly what is being debated, what amendments are pending, and how the vote will be conducted.',
      },
    ],
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Set the Stage',
    detail:
      'Open the meeting setup wizard to set attendance, quorum, agenda, and session options before you begin.',
  },
  {
    n: '2',
    title: 'Run the Cockpit',
    detail:
      'Launch the meeting. The AI Chair guides the phrasing while the Secretary records live annotations and vote counts.',
  },
  {
    n: '3',
    title: 'Export & Adjourn',
    detail:
      'Click Adjourn. Download clean, export-ready meeting minutes ready for distribution.',
  },
];

const AUDIENCES = [
  {
    title: 'HOA Boards & Neighborhood Associations',
    detail: 'End 3-hour annual meetings on time.',
  },
  {
    title: 'City Councils & Local Government',
    detail: 'Maintain strict parliamentary procedure with public transparency.',
  },
  {
    title: 'Local Political Parties',
    detail: 'Keep caucuses and conventions orderly with clear process everyone can follow.',
  },
  {
    title: 'Non-Profit & University Boards',
    detail: 'Streamline committee referrals and trustee votes.',
  },
  {
    title: 'Student Government & Greek Life',
    detail: 'Teach young leaders proper procedure with zero friction.',
  },
];

const BOARD_DEMO_MAIL =
  'mailto:hello@pocketparliamentarian.com?subject=Custom%20Board%20Demo';

const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Solo chair, limited use',
    features: [
      'Meeting console with RONR motion engine',
      'Room / guest display (no QR voting)',
      'Export minutes from the current meeting',
      'Up to 3 meetings per month',
      'Solo mode only (no Chair Dashboard split)',
      'Elections locked',
    ],
    cta: { label: 'Create free org', to: '/login?mode=signup', kind: 'link' },
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Run tonight’s meeting end-to-end',
    featured: true,
    features: [
      'Everything in Free, plus unlimited meetings',
      'Assisted mode + Chair Dashboard',
      'QR device voting',
      'Elections (capped offices)',
      'Light Meeting Log (recent meetings)',
      'Committees module for assigned work',
    ],
    cta: { label: 'Start with Pro', to: '/login?mode=signup', kind: 'link' },
  },
  {
    id: 'institutional',
    name: 'Institutional',
    tagline: 'Own the official record',
    features: [
      'Everything in Pro',
      'Full Meeting Log: multi-year library, search, approve, share',
      'Branded room display',
      'Audit log',
      'Concurrent sessions (board + committee)',
      'Priority support and annual invoicing',
    ],
    cta: { label: 'Schedule a board demo', href: BOARD_DEMO_MAIL, kind: 'mail' },
  },
];

/**
 * Public marketing homepage for pocketparliamentarian.com.
 */
export default function LandingPage() {
  return (
    <div className="landing-root font-landing text-[var(--land-ink)] antialiased">
      <style>{`
        .landing-root {
          --land-ink: #0b1220;
          --land-muted: #5b6b86;
          --land-paper: #f5f7fb;
          --land-blue: #1d4ed8;
          --land-blue-deep: #1e3a8a;
          --land-blue-soft: #dbe7ff;
          --land-sky: #e8eef9;
          --land-line: rgba(11, 18, 32, 0.1);
        }
        @keyframes land-rise {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes land-drift {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(1.5%, -1%, 0) scale(1.03); }
        }
        .land-rise { animation: land-rise 0.8s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .land-rise-d1 { animation: land-rise 0.85s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both; }
        .land-rise-d2 { animation: land-rise 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.18s both; }
        .land-rise-d3 { animation: land-rise 0.95s cubic-bezier(0.22, 1, 0.36, 1) 0.26s both; }
        .land-drift { animation: land-drift 24s ease-in-out infinite; }
        .land-cta-primary {
          transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
        }
        .land-cta-primary:hover {
          transform: translateY(-1px);
          background-color: #1e40af;
          box-shadow: 0 10px 28px rgba(29, 78, 216, 0.32);
        }
        .land-cta-secondary {
          transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }
        .land-cta-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.65);
        }
        .land-cta-ghost {
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }
        .land-cta-ghost:hover {
          background-color: var(--land-sky);
          border-color: #93b4f5;
        }
        @media (prefers-reduced-motion: reduce) {
          .land-rise, .land-rise-d1, .land-rise-d2, .land-rise-d3, .land-drift {
            animation: none;
          }
        }
      `}</style>

      {/* Hero */}
      <header className="relative min-h-[100svh] overflow-hidden bg-[var(--land-ink)] text-white">
        <div
          className="land-drift pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background: `
              radial-gradient(ellipse 85% 65% at 78% 12%, rgba(37, 99, 235, 0.5) 0%, transparent 55%),
              radial-gradient(ellipse 55% 45% at 12% 78%, rgba(30, 58, 138, 0.55) 0%, transparent 50%),
              linear-gradient(165deg, #0b1220 0%, #122038 42%, #0a1020 100%)
            `,
          }}
        />
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.16]"
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden
        >
          <defs>
            <linearGradient id="landRail" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#93c5fd" stopOpacity="0" />
              <stop offset="50%" stopColor="#93c5fd" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#93c5fd" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0 560 Q300 480 600 560 T1200 560" fill="none" stroke="url(#landRail)" strokeWidth="1.5" />
          <path d="M0 600 Q300 520 600 600 T1200 600" fill="none" stroke="#fff" strokeOpacity="0.1" strokeWidth="1" />
          <path d="M0 640 Q300 560 600 640 T1200 640" fill="none" stroke="#fff" strokeOpacity="0.07" strokeWidth="1" />
          <rect x="90" y="140" width="240" height="10" rx="2" fill="#93c5fd" fillOpacity="0.35" />
          <rect x="90" y="168" width="180" height="7" rx="2" fill="#fff" fillOpacity="0.12" />
          <rect x="90" y="190" width="210" height="7" rx="2" fill="#fff" fillOpacity="0.08" />
          <circle cx="980" cy="210" r="5" fill="#60a5fa" fillOpacity="0.7" />
          <circle cx="1020" cy="210" r="5" fill="#fff" fillOpacity="0.25" />
          <circle cx="1060" cy="210" r="5" fill="#fff" fillOpacity="0.15" />
          <line x1="985" y1="210" x2="1015" y2="210" stroke="#fff" strokeOpacity="0.2" strokeWidth="2" />
          <line x1="1025" y1="210" x2="1055" y2="210" stroke="#fff" strokeOpacity="0.12" strokeWidth="2" />
        </svg>

        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-5xl flex-col px-4 pb-14 pt-6 sm:px-10 sm:pb-16 sm:pt-8">
          <nav className="land-rise flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-sm font-semibold tracking-tight text-white/90">
              Pocket Parliamentarian
            </p>
            <Link
              to="/login"
              className="land-cta-secondary shrink-0 rounded-xl border border-white/35 px-3.5 py-2 text-sm font-semibold text-white sm:px-4"
            >
              Log in
            </Link>
          </nav>

          <div className="mt-auto flex max-w-3xl flex-col pb-8 pt-16 sm:pb-16 sm:pt-28">
            <p className="land-rise text-[1.65rem] font-semibold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
              Pocket Parliamentarian
            </p>
            <p className="land-rise-d1 mt-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-200/90 sm:mt-5 sm:text-xs sm:tracking-[0.18em]">
              Parliamentary procedure, automated
            </p>
            <h1 className="land-rise-d1 mt-3 text-[1.65rem] font-semibold leading-[1.15] tracking-tight text-white sm:mt-4 sm:text-4xl sm:leading-[1.12] md:text-5xl">
              Never Lose Control of a Meeting Again.
            </h1>
            <p className="land-rise-d2 mt-5 max-w-2xl text-[0.95rem] leading-relaxed text-white/70 sm:mt-6 sm:text-lg">
              The first meeting platform with a built-in Robert’s Rules of Order logic engine. Run
              flawless parliamentary procedure, guide your Chair with real-time scripts, and generate
              perfect, export-ready minutes in real time.
            </p>
            <div className="land-rise-d3 mt-8 flex w-full flex-col gap-3 sm:mt-10 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
              <Link
                to="/login?mode=signup"
                className="land-cta-primary inline-flex w-full items-center justify-center rounded-xl bg-[var(--land-blue)] px-6 py-3.5 text-sm font-semibold text-white sm:w-auto sm:text-base"
              >
                Start Your Free Trial
              </Link>
              <Link
                to="/login"
                className="land-cta-secondary inline-flex w-full items-center justify-center rounded-xl border border-white/40 px-6 py-3.5 text-center text-sm font-semibold text-white sm:w-auto sm:text-base"
              >
                Log in
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Problem / contrast */}
      <section className="bg-[var(--land-paper)] px-4 py-16 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="max-w-2xl text-2xl font-semibold tracking-tight text-[var(--land-ink)] sm:text-3xl md:text-4xl">
            Meetings shouldn’t feel like a 400-page pop quiz.
          </h2>

          {/* Mobile: stacked labeled pairs — clearer than a squeezed 2-col table */}
          <ul className="mt-10 space-y-4 sm:hidden">
            {OLD_VS_PARLI.map((row, i) => (
              <li
                key={i}
                className="overflow-hidden rounded-2xl border border-[var(--land-line)] bg-white"
              >
                <div className="border-b border-[var(--land-line)] bg-[var(--land-paper)] px-4 py-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--land-muted)]">
                    The Old Way
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[var(--land-muted)]">{row.old}</p>
                </div>
                <div className="bg-[var(--land-blue-soft)]/40 px-4 py-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--land-blue-deep)]">
                    The PocketParliamentarian Way
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[var(--land-ink)]">{row.parli}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop / tablet: two-column comparison */}
          <div className="mt-12 hidden overflow-hidden rounded-2xl border border-[var(--land-line)] bg-white sm:block">
            <div className="grid grid-cols-2 bg-[var(--land-blue-soft)] text-sm font-semibold text-[var(--land-blue-deep)]">
              <div className="border-r border-[var(--land-line)] px-6 py-3.5">The Old Way</div>
              <div className="px-6 py-3.5">The PocketParliamentarian Way</div>
            </div>
            <ul>
              {OLD_VS_PARLI.map((row, i) => (
                <li
                  key={i}
                  className={`grid grid-cols-2 ${
                    i < OLD_VS_PARLI.length - 1 ? 'border-b border-[var(--land-line)]' : ''
                  }`}
                >
                  <div className="border-r border-[var(--land-line)] px-6 py-6 text-sm leading-relaxed text-[var(--land-muted)]">
                    {row.old}
                  </div>
                  <div className="px-6 py-6 text-sm leading-relaxed text-[var(--land-ink)]">
                    {row.parli}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature pillars */}
      <section className="bg-white px-4 py-16 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--land-blue)]">
            Core capabilities
          </p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-[var(--land-ink)] sm:text-3xl md:text-4xl">
            What Pocket Parliamentarian does
          </h2>

          <div className="mt-12 space-y-14 sm:mt-16 sm:space-y-20">
            {PILLARS.map((pillar) => (
              <article key={pillar.label}>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--land-muted)]">
                  {pillar.label}
                </p>
                <h3 className="mt-2 text-xl font-semibold tracking-tight text-[var(--land-ink)] sm:text-2xl md:text-3xl">
                  {pillar.header}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-[var(--land-muted)] sm:text-lg">
                  {pillar.copy}
                </p>
                <ul className="mt-8 space-y-5 border-t border-[var(--land-line)] pt-8">
                  {pillar.points.map((point) => (
                    <li key={point.title}>
                      <p className="text-base font-semibold text-[var(--land-ink)]">{point.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-[var(--land-muted)] sm:text-base">
                        {point.detail}
                      </p>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[var(--land-sky)] px-4 py-16 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-semibold tracking-tight text-[var(--land-ink)] sm:text-3xl md:text-4xl">
            How it works
          </h2>
          <p className="mt-3 text-base text-[var(--land-muted)] sm:text-lg">Three simple steps.</p>
          <ol className="mt-10 space-y-8 sm:mt-12 sm:space-y-10">
            {STEPS.map((step) => (
              <li key={step.n} className="flex gap-4 sm:gap-5">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--land-blue)] text-sm font-bold text-white sm:h-10 sm:w-10"
                  aria-hidden
                >
                  {step.n}
                </span>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold tracking-tight text-[var(--land-ink)] sm:text-xl">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--land-muted)] sm:text-base">
                    {step.detail}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Target audiences */}
      <section className="bg-white px-4 py-16 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--land-blue)]">
            Built for
          </p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-[var(--land-ink)] sm:text-3xl md:text-4xl">
            Who it’s for
          </h2>
          <ul className="mt-10 space-y-7 border-t border-[var(--land-line)] pt-8 sm:mt-12 sm:space-y-8 sm:pt-10">
            {AUDIENCES.map((audience) => (
              <li key={audience.title}>
                <p className="text-base font-semibold tracking-tight text-[var(--land-ink)] sm:text-lg">
                  {audience.title}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-[var(--land-muted)] sm:text-base">
                  {audience.detail}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-[var(--land-paper)] px-4 py-16 sm:px-10 sm:py-28">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--land-blue)]">
            Pricing
          </p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-[var(--land-ink)] sm:text-3xl md:text-4xl">
            Plans for every board
          </h2>
          <p className="mt-3 max-w-2xl text-base text-[var(--land-muted)] sm:text-lg">
            Plans are coming soon—create an organization today and you’ll be ready when billing
            opens. Feature split below is our intended roadmap.
          </p>

          <div className="mt-12 grid gap-4 lg:grid-cols-3">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`flex flex-col rounded-2xl border bg-white p-6 shadow-pp ${
                  tier.featured
                    ? 'border-[var(--land-blue)] ring-2 ring-[var(--land-blue)]/20'
                    : 'border-[var(--land-line)]'
                }`}
              >
                {tier.featured ? (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--land-blue)]">
                    Most popular
                  </p>
                ) : (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-transparent">
                    Tier
                  </p>
                )}
                <h3 className="mt-2 text-xl font-semibold tracking-tight text-[var(--land-ink)]">
                  {tier.name}
                </h3>
                <p className="mt-1 text-sm text-[var(--land-muted)]">{tier.tagline}</p>
                <ul className="mt-6 flex-1 space-y-3 border-t border-[var(--land-line)] pt-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="text-sm leading-snug text-[var(--land-ink)]">
                      <span className="mr-2 text-[var(--land-blue)]" aria-hidden>
                        ·
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
                {tier.cta.kind === 'mail' ? (
                  <a
                    href={tier.cta.href}
                    className="land-cta-ghost mt-8 inline-flex w-full items-center justify-center rounded-xl border border-[var(--land-line)] bg-[var(--land-paper)] px-4 py-3 text-sm font-semibold text-[var(--land-ink)]"
                  >
                    {tier.cta.label}
                  </a>
                ) : (
                  <Link
                    to={tier.cta.to}
                    className={`mt-8 inline-flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold ${
                      tier.featured
                        ? 'land-cta-primary bg-[var(--land-blue)] text-white'
                        : 'land-cta-ghost border border-[var(--land-line)] bg-[var(--land-paper)] text-[var(--land-ink)]'
                    }`}
                  >
                    {tier.cta.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative overflow-hidden bg-[var(--land-ink)] px-4 py-16 text-white sm:px-10 sm:py-28">
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          aria-hidden
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 80% 20%, rgba(37, 99, 235, 0.35) 0%, transparent 55%)',
          }}
        />
        <div className="relative mx-auto max-w-3xl">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
            Ready to run your next meeting like a Swiss watch?
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            Join hundreds of Chairs, Secretaries, and Parliamentarians who have taken the stress out
            of Robert’s Rules.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center">
            <Link
              to="/login?mode=signup"
              className="land-cta-primary inline-flex w-full items-center justify-center rounded-xl bg-[var(--land-blue)] px-6 py-3.5 text-sm font-semibold text-white sm:w-auto sm:text-base"
            >
              Start Free Trial
            </Link>
            <a
              href={BOARD_DEMO_MAIL}
              className="text-center text-sm font-semibold text-white/80 underline-offset-4 hover:text-white hover:underline sm:text-left sm:text-base"
            >
              Schedule a Custom Board Demo
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[var(--land-ink)] px-4 py-8 text-white sm:px-10 sm:py-10">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div>
            <p className="text-base font-semibold tracking-tight sm:text-lg">Pocket Parliamentarian</p>
            <p className="mt-1 text-xs text-white/50">
              © {new Date().getFullYear()} Pocket Parliamentarian. All rights reserved.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/login" className="text-white/75 underline-offset-4 hover:text-white hover:underline">
              Log in
            </Link>
            <Link
              to="/login?mode=signup"
              className="text-white/75 underline-offset-4 hover:text-white hover:underline"
            >
              Free trial
            </Link>
            <Link to="/terms" className="text-white/75 underline-offset-4 hover:text-white hover:underline">
              Terms
            </Link>
            <Link to="/privacy" className="text-white/75 underline-offset-4 hover:text-white hover:underline">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
